import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, Delete as DeleteIcon, Cancel as CancelIcon } from '@mui/icons-material';

interface SaveDiscardDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  onDiscard: () => void;
  isSaving?: boolean;
}

const SaveDiscardDialog: React.FC<SaveDiscardDialogProps> = ({
  open,
  onClose,
  onSave,
  onDiscard,
  isSaving = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          px: 2.5,
          background: 'linear-gradient(135deg, #6C63FF 0%, #FF6CAB 100%)',
          color: 'white',
          fontSize: '0.95rem',
          fontWeight: 600,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
          }}
        >
          💾
        </Box>
        Unsaved Changes
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, pb: 2, pt: 3 }}>
        <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
          You have unsaved changes to your business profile. What would you like to do?
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Your changes will be lost if you discard them.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isSaving}
          startIcon={<CancelIcon />}
          sx={{
            px: 2.5,
            py: 0.75,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            borderColor: 'grey.300',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'grey.400',
              backgroundColor: 'rgba(0,0,0,0.04)',
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onDiscard}
          variant="outlined"
          disabled={isSaving}
          startIcon={<DeleteIcon />}
          sx={{
            px: 2.5,
            py: 0.75,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            borderColor: 'error.main',
            color: 'error.main',
            '&:hover': {
              borderColor: 'error.dark',
              backgroundColor: 'error.light',
              color: 'error.dark',
            }
          }}
        >
          Discard Changes
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          sx={{
            px: 2.5,
            py: 0.75,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            background: 'linear-gradient(135deg, #6C63FF 0%, #FF6CAB 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5A52E6 0%, #E55A8A 100%)',
            },
            '&:disabled': {
              background: 'rgba(108, 99, 255, 0.5)',
              color: 'rgba(255, 255, 255, 0.7)',
            }
          }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveDiscardDialog;

